import React, { useCallback, useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';

import './peer-left-modal.css';


// Queue-entry kinds. 'left' = explicit Leave Game (PeerLeftMidGame);
// 'disconnected' = silent socket drop / tab close (PeerDisconnectedMidGame,
// CC_PEER_LEFT_SILENT). Same owner-only modal, different copy + affordances.
const KIND_LEFT = 'left';
const KIND_DISCONNECTED = 'disconnected';


export default function PeerLeftModal({ app, isGroupLeader }) {
    // CC_LOBBY_GAME_CRUD (2026-05-27): owner-only modal that fires when
    // a peer leaves a PLAYING lobby. Backend posts PeerLeftMidGame to
    // the owner's channel; GameGroupHandler dispatches a window
    // 'peer-left-mid-game-event'. Owner picks Replace-with-AI or
    // Continue — both keep the game alive (replace puts an AI on the
    // empty seat, Continue keeps the seat open and lets the game wait).
    // CC_LOBBY_SAVE_FINISH (2026-06-03), F5: two peers leaving in rapid
    // succession used to clobber the single `peer` slot, stranding one
    // empty seat with no owner affordance. Queue departures instead;
    // Replace/Continue dismisses the head and the next pops up.
    // CC_PEER_LEFT_SILENT (2026-06-03): a peer who closes their tab
    // (socket drop, not an explicit leave) now also surfaces here via
    // PeerDisconnectedMidGame → 'peer-disconnected-mid-game-event'. Same
    // queue, tagged with `kind` so the render can distinguish "left"
    // from "disconnected" copy + affordances. DISCORD_KNOWLEDGE §6.5
    // had this as a known gap (owner got no signal at all on a drop).
    const [pendingPeers, setPendingPeers] = useState([]);
    const peer = pendingPeers[0] || null;

    // CC_PATIENT_SEAT_OWNER_UI (2026-06-03): owner detection works under
    // both the legacy isGroupLeader prop (Lobby.js mount) and the new
    // app-derived check (global App.js mount, where no prop is passed).
    const propOwnerProvided = typeof isGroupLeader === 'boolean';

    // CC_DISCONNECT_VISIBILITY (2026-06-07, Symptom A): resolve owner-ness
    // LIVE, at the moment an event fires — never frozen at mount.
    //
    // The bug: this modal is globally mounted in App.js with no
    // isGroupLeader prop, so owner-ness derived from
    // groupHandler.isGroupLeader(). The old effect gated *listener
    // registration* on that derived value read at mount time. On the
    // Discord rail the first render happens before the first Members
    // broadcast settles owner identity (memberRecord has no `.user`, or
    // _ownerUserId is still null), so isGroupLeader() returns false and
    // NO listeners were attached. Nothing re-renders this modal when
    // identity later settles (it has no group-update-event subscription
    // and its only state is the queue), so the effect never re-ran and
    // the listeners were never attached. PeerDisconnectedMidGame fired,
    // GameGroupHandler dispatched the window event, and it landed on the
    // floor — "event fired but nothing happened." The fix decouples
    // registration from the gate: listeners are ALWAYS attached, and the
    // owner check is re-evaluated when the event actually fires (well
    // after identity settles), inside the handler.
    const isOwnerNow = useCallback(() => {
        if (propOwnerProvided) return isGroupLeader;
        const groupHandler = (
            app && app.game && app.game.groupHandler) || null;
        return Boolean(
            groupHandler
            && typeof groupHandler.isGroupLeader === 'function'
            && groupHandler.isGroupLeader());
    }, [app, isGroupLeader, propOwnerProvided]);

    useEffect(() => {
        const makeHandler = (kind) => (event) => {
            // Live owner gate: a non-owner never enqueues, so the modal
            // never surfaces for peers — but the LISTENER is always
            // bound, so a settled-late owner is no longer missed.
            if (!isOwnerNow()) return;
            const detail = (event && event.detail) || {};
            if (detail.user_id === undefined || detail.user_id === null) {
                return;
            }
            setPendingPeers((prev) => [...prev, {
                userId: detail.user_id,
                displayName: detail.display_name || 'Player',
                seat: detail.seat,
                kind: kind,
            }]);
        };
        // CC_MIDGAME_SEAT_PRESENCE (2026-06-08): when a peer who left /
        // dropped comes BACK (a seat-keyed rejoin), the backend posts
        // PeerRejoinedMidGame → 'peer-rejoined-mid-game-event'. Their seat
        // is no longer empty, so drop any pending entry for that user from
        // the queue — the "they left — Replace with AI?" prompt should
        // close itself rather than make the owner dismiss a stale choice.
        // No owner gate needed: a non-owner has an empty queue, so the
        // filter is a harmless no-op.
        const rejoinHandler = (event) => {
            const detail = (event && event.detail) || {};
            if (detail.user_id === undefined || detail.user_id === null) {
                return;
            }
            setPendingPeers((prev) =>
                prev.filter((p) => p.userId !== detail.user_id));
        };
        const leftHandler = makeHandler(KIND_LEFT);
        const disconnectedHandler = makeHandler(KIND_DISCONNECTED);
        window.addEventListener('peer-left-mid-game-event', leftHandler);
        window.addEventListener(
            'peer-disconnected-mid-game-event', disconnectedHandler);
        window.addEventListener(
            'peer-rejoined-mid-game-event', rejoinHandler);
        return () => {
            window.removeEventListener(
                'peer-left-mid-game-event', leftHandler);
            window.removeEventListener(
                'peer-disconnected-mid-game-event', disconnectedHandler);
            window.removeEventListener(
                'peer-rejoined-mid-game-event', rejoinHandler);
        };
    }, [isOwnerNow]);

    // Pop the head of the queue. If more departures remain, the modal
    // stays open showing the next peer.
    const dismiss = useCallback(
        () => setPendingPeers((prev) => prev.slice(1)), []);

    const onReplace = useCallback(() => {
        if (!app || !app.game || !app.game.gameApi) return;
        if (peer === null) return;
        app.game.gameApi.replaceSeatWithAI(peer.seat);
        dismiss();
    }, [app, peer, dismiss]);

    // Disconnect-variant only: persist the game and return to the Main
    // Menu (mirrors the Save & Exit path in Lobby.js / EscapeMenu).
    const onSaveAndMoveOn = useCallback(() => {
        if (!app || !app.game || !app.game.gameApi) return;
        app.game.gameApi.saveGame();
        dismiss();
        window.dispatchEvent(
            new CustomEvent('quick-router-event', { detail: '/' }));
    }, [app, dismiss]);

    // CC_LIVING_GAMES_LIST_DESIGN (decided behavior #6): "Make Public"
    // surfaces the in-game public dial as a modal action — go public so
    // the games list can recruit a replacement while the seat stays
    // patient. Same Group/Configure rail as the EscapeMenu dial
    // (top-level options.public, which configure_group reads). Dismisses
    // the modal; the game plays on.
    const onMakePublic = useCallback(() => {
        if (!app || !app.game || !app.game.gameApi) return;
        app.game.gameApi.configureGroupOptions({ public: true });
        dismiss();
    }, [app, dismiss]);

    if (peer === null) return null;

    const seatLabel = typeof peer.seat === 'number'
        ? `Seat ${peer.seat + 1}`
        : 'Their seat';
    const isDisconnected = peer.kind === KIND_DISCONNECTED;

    // PLACEHOLDER COPY (CC_PEER_LEFT_SILENT) — Michael owns final strings.
    // The disconnect variant intentionally differs from the explicit-leave
    // variant: a dropped peer may reconnect, so the framing is softer and
    // the default affordance is "Wait" (the patient seat) rather than
    // "Continue".
    const title = isDisconnected
        ? `${peer.displayName} lost connection`
        : `${peer.displayName} left the game`;

    return (
        <Modal
            show
            onHide={dismiss}
            backdrop="static"
            keyboard={false}
            centered
            className="peer-left-modal"
        >
            <Modal.Header>
                <Modal.Title>
                    {title}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p>
                    {seatLabel} is empty. Pick one:
                </p>
                {isDisconnected ? (
                    <ul className="peer-left-modal-explainer">
                        <li>
                            <strong>Wait</strong> — keep the seat open. They
                            may reconnect; the game waits on their turn.
                        </li>
                        <li>
                            <strong>Replace with AI</strong> — the game
                            continues; an AI takes the empty seat.
                        </li>
                        <li>
                            <strong>Make Public</strong> — list the game on the
                            public games list so a new player can take the seat
                            while you keep playing.
                        </li>
                        <li>
                            <strong>Save &amp; Move On</strong> — save the
                            game for everyone and return to the Main Menu.
                        </li>
                    </ul>
                ) : (
                    <ul className="peer-left-modal-explainer">
                        <li>
                            <strong>Replace with AI</strong> — the game
                            continues; an AI takes the empty seat.
                        </li>
                        <li>
                            <strong>Make Public</strong> — list the game on the
                            public games list so a new player can take the seat
                            while you keep playing.
                        </li>
                        <li>
                            <strong>Continue</strong> — keep the seat open. The game
                            plays on; when it&apos;s their turn it will wait for them.
                        </li>
                    </ul>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="hex" onClick={onReplace}>
                    Replace with AI
                </Button>
                {/* CC_LIVING_GAMES_LIST_DESIGN: "Make Public" is the in-game
                  * dial as a modal action (decided behavior #6). PLACEHOLDER
                  * copy — Michael owns the final wording. */}
                <Button variant="stem" onClick={onMakePublic}>
                    Make Public
                </Button>
                {isDisconnected ? (
                    <>
                        <Button variant="seed" onClick={dismiss}>
                            Wait
                        </Button>
                        <Button variant="dread" onClick={onSaveAndMoveOn}>
                            Save &amp; Move On
                        </Button>
                    </>
                ) : (
                    <Button variant="seed" onClick={dismiss}>
                        Continue
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
}
